import { FuelRobDataTypeEnum, NoonReportSectionEnum, NoonReportStatusEnum, NoonReportTypeEnum } from '../types/ViewModel/AolEnums';
import { AolDataItem, AolGroup } from '../types/ViewModel/AolItem';
import { createSectionData, getNumberValue } from './aolFieldsHelper';
import { NoonReportSection, ValidatedNoonReport } from '../types/ViewModel/ValidatedNoonReport';
import { NoonReportSectionData } from '../types/ViewModel/NoonReportSectionData';
import {
    NoonReportActivitiesSectionFactory,
    NoonReportBridgeSectionFactory,
    NoonReportCargoHeatingSectionFactory,
    NoonReportCommentsSectionFactory,
    NoonReportEnginesSectionFactory,
    NoonReportFreshWaterSectionFactory,
    NoonReportFuelRobSectionFactory,
    NoonReportGeneralSectionFactory,
    NoonReportInertGasProductionSectionFactory,
    NoonReportMrvSectionFactory,
    NoonReportPowerpacksSectionFactory,
    NoonReportTankCleaningSectionFactory,
    NoonReportWeatherSectionFactory,
} from '../factory/ValidatedNoonReportFactory';
import { ValidatedNoonReportBM } from '../types/BusinessModel/ValidatedNoonReportBM';
import { prettifyHours } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isDateAfter, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { isNullOrUndefined } from './objectUtils';

const noonReportActivitiesSectionFactory = new NoonReportActivitiesSectionFactory();
const noonReportBridgeSectionFactory = new NoonReportBridgeSectionFactory();
const noonReportCargoHeatingSectionFactory = new NoonReportCargoHeatingSectionFactory();
const noonReportCommentsSectionFactory = new NoonReportCommentsSectionFactory();
const noonReportEnginesSectionFactory = new NoonReportEnginesSectionFactory();
const noonReportFreshWaterSectionFactory = new NoonReportFreshWaterSectionFactory();
const noonReportFuelRobSectionFactory = new NoonReportFuelRobSectionFactory();
const noonReportGeneralSectionFactory = new NoonReportGeneralSectionFactory();
const noonReportInertGasProductionSectionFactory = new NoonReportInertGasProductionSectionFactory();
const noonReportMrvSectionFactory = new NoonReportMrvSectionFactory();
const noonReportPowerpacksSectionFactory = new NoonReportPowerpacksSectionFactory();
const noonReportTankCleaningSectionFactory = new NoonReportTankCleaningSectionFactory();
const noonReportWeatherSectionFactory = new NoonReportWeatherSectionFactory();

export const getAolSectionData = (
    noonReport: ValidatedNoonReport | undefined,
    sectionType: NoonReportSectionEnum
): NoonReportSectionData | undefined => {
    switch (sectionType) {
        case NoonReportSectionEnum.Activities:
            return noonReport?.activitiesSection?.sectionData;
        case NoonReportSectionEnum.Bridge:
            return noonReport?.bridgeSection?.sectionData;
        case NoonReportSectionEnum.Comments:
            return noonReport?.commentsSection?.sectionData;
        case NoonReportSectionEnum.Engines:
            return noonReport?.enginesSection?.sectionData;
        case NoonReportSectionEnum.FreshWater:
            return noonReport?.freshWaterSection?.sectionData;
        case NoonReportSectionEnum.FuelRob:
            return noonReport?.fuelRobSection?.sectionData;
        case NoonReportSectionEnum.General:
            return noonReport?.generalSection?.sectionData;
        case NoonReportSectionEnum.InertGasProduction:
            return noonReport?.inertGasProductionSection?.sectionData;
        case NoonReportSectionEnum.PowerPacks:
            return noonReport?.powerpacksSection?.sectionData;
        case NoonReportSectionEnum.TankCleaning:
            return noonReport?.tankCleaningSection?.sectionData;
        case NoonReportSectionEnum.CargoTemperatureControl:
            return noonReport?.cargoHeatingSection?.sectionData;
        case NoonReportSectionEnum.Weather:
            return noonReport?.weatherSection?.sectionData;
    }
};

export const aolSectionString = (section: NoonReportSectionEnum) => {
    switch (section) {
        case NoonReportSectionEnum.Activities:
            return 'Activities & Events';
        case NoonReportSectionEnum.Bridge:
            return 'Bridge';
        case NoonReportSectionEnum.Comments:
            return 'Comments';
        case NoonReportSectionEnum.Engines:
            return 'Engines';
        case NoonReportSectionEnum.FreshWater:
            return 'Fresh Water';
        case NoonReportSectionEnum.FuelRob:
            return 'Fuel ROB';
        case NoonReportSectionEnum.General:
            return 'General';
        case NoonReportSectionEnum.InertGasProduction:
            return 'Inert Gas Production';
        case NoonReportSectionEnum.PowerPacks:
            return 'Power Packs';
        case NoonReportSectionEnum.TankCleaning:
            return 'Tank Preparation';
        case NoonReportSectionEnum.CargoTemperatureControl:
            return 'Cargo Temperature Control';
        case NoonReportSectionEnum.Weather:
            return 'Weather';
    }
};

export const reportTypeString = (type: NoonReportTypeEnum) => {
    if (type === NoonReportTypeEnum.Arrival) return 'Arrival';
    if (type === NoonReportTypeEnum.Departure) return 'Departure';
    if (type === NoonReportTypeEnum.Port) return 'Port';
    if (type === NoonReportTypeEnum.Sea) return 'Sea';
    return '';
};

export const aolTypeDescriptionText = (aolGroup: AolGroup) => {
    let descriptionText = '';

    if (aolGroup.aolDataItems?.length > 0) {
        if (!aolGroup.isValid) {
            descriptionText += 'INVALID ! ';
        }
        aolGroup.aolDataItems.forEach((aolDataItem, i) => {
            if (aolDataItem.reportType === NoonReportTypeEnum.Sea) {
                const idleOffsetHours = getNumberValue(aolDataItem.noonReport?.generalSection?.idlOffsetHours);
                descriptionText += !isNullOrUndefined(idleOffsetHours) && idleOffsetHours !== 0 ? 'Sea (IDL crossed)' : 'At Sea';
            } else if (aolDataItem.reportType === NoonReportTypeEnum.Arrival) {
                descriptionText += aolDataItem.portName ? 'Arrival ' + aolDataItem.portName : 'Arrival';
            } else if (aolDataItem.reportType === NoonReportTypeEnum.Departure) {
                descriptionText += aolDataItem.portName ? 'Departure ' + aolDataItem.portName : 'Departure';
            } else if (aolDataItem.reportType === NoonReportTypeEnum.Port) {
                descriptionText += aolDataItem.portName ?? '';
            }
            if (i < aolGroup.aolDataItems?.length - 1) {
                descriptionText += ' | ';
            }
        });
    }

    return descriptionText;
};

export const aolHoursText = (aolGroup: AolGroup) => {
    let hoursText = prettifyHours(aolGroup.aolHours);

    aolGroup.aolDataItems.forEach((aolDataItem) => {
        if (aolDataItem.reportType === NoonReportTypeEnum.Sea) {
            const manualOffsetHours = getNumberValue(aolDataItem.noonReport?.generalSection?.manualOffsetHours);
            const idlOffsetHours = getNumberValue(aolDataItem.noonReport?.generalSection?.idlOffsetHours);
            const timeZoneChangeHours = aolDataItem.aolItem?.timeZoneChangeHours ?? 0;
            const dstOffsetHours = aolDataItem.aolItem?.dstOffsetHours ?? 0;
            const offsetHours = (manualOffsetHours ?? 0) + (idlOffsetHours ?? 0) + timeZoneChangeHours + dstOffsetHours;
            if (offsetHours !== 0) {
                hoursText += offsetHours > 0 ? ' (+' + prettifyHours(offsetHours) + ')' : ' (-' + prettifyHours(Math.abs(offsetHours)) + ')';
            }
        }
    });
    return hoursText;
};

export const hasBunkering = (aolDataItem: AolDataItem): boolean => {
    const fuelRobDatas = aolDataItem.aolItem?.fuelRobDatas;
    const hasBunkering = fuelRobDatas && fuelRobDatas.filter((o) => o.type === FuelRobDataTypeEnum.MeasureAfterBunkering).length > 0;
    return hasBunkering ?? false;
};

export const aolFilterNoonReportStatus = (page: string, status: NoonReportStatusEnum, aolGroupDate: string, aolFilterFromDate: Date) => {
    if (isDateBefore(aolGroupDate, aolFilterFromDate)) {
        return false;
    }
    if (page === 'reporting') {
        return (
            status === NoonReportStatusEnum.Missing || status === NoonReportStatusEnum.Edited || status === NoonReportStatusEnum.TankersEventMismatch
        );
    } else if (page === 'approval') {
        return status === NoonReportStatusEnum.Completed;
    } else if (page === 'approved') {
        return status === NoonReportStatusEnum.Approved;
    }
};

const getDefaultSection = (noonReportSectionEnum: NoonReportSectionEnum): NoonReportSection => {
    return {
        sectionData: createSectionData(noonReportSectionEnum),
    };
};
export const getAllAolSections = (noonReport: ValidatedNoonReport): any[] => {
    if (!noonReport) return [];
    return [
        noonReport.activitiesSection ?? getDefaultSection(NoonReportSectionEnum.Activities),
        noonReport.bridgeSection ?? getDefaultSection(NoonReportSectionEnum.Bridge),
        noonReport.cargoHeatingSection ?? getDefaultSection(NoonReportSectionEnum.CargoTemperatureControl),
        noonReport.commentsSection ?? getDefaultSection(NoonReportSectionEnum.Comments),
        noonReport.enginesSection ?? getDefaultSection(NoonReportSectionEnum.Engines),
        noonReport.freshWaterSection ?? getDefaultSection(NoonReportSectionEnum.FreshWater),
        noonReport.fuelRobSection && noonReport?.metadata?.aolVersion >= 5 ? getDefaultSection(NoonReportSectionEnum.FuelRob) : null,
        noonReport.generalSection ?? getDefaultSection(NoonReportSectionEnum.General),
        noonReport.inertGasProductionSection ?? getDefaultSection(NoonReportSectionEnum.InertGasProduction),
        noonReport.powerpacksSection ?? getDefaultSection(NoonReportSectionEnum.PowerPacks),
        noonReport.tankCleaningSection ?? getDefaultSection(NoonReportSectionEnum.TankCleaning),
        noonReport.weatherSection ?? getDefaultSection(NoonReportSectionEnum.Weather),
    ].filter((section) => section !== null);
};

export const GetSection = (sectionType: NoonReportSectionEnum, noonReport: ValidatedNoonReport) => {
    switch (sectionType) {
        case NoonReportSectionEnum.Activities:
            return noonReport.activitiesSection;
        case NoonReportSectionEnum.Bridge:
            return noonReport.bridgeSection;
        case NoonReportSectionEnum.CargoTemperatureControl:
            return noonReport.cargoHeatingSection;
        case NoonReportSectionEnum.Comments:
            return noonReport.commentsSection;
        case NoonReportSectionEnum.Engines:
            return noonReport.enginesSection;
        case NoonReportSectionEnum.FreshWater:
            return noonReport.freshWaterSection;
        case NoonReportSectionEnum.FuelRob:
            return noonReport.fuelRobSection;
        case NoonReportSectionEnum.General:
            return noonReport.generalSection;
        case NoonReportSectionEnum.InertGasProduction:
            return noonReport.inertGasProductionSection;
        case NoonReportSectionEnum.PowerPacks:
            return noonReport.powerpacksSection;
        case NoonReportSectionEnum.TankCleaning:
            return noonReport.tankCleaningSection;
        case NoonReportSectionEnum.Weather:
            return noonReport.weatherSection;
    }
    return;
};

export const GetSectionBM = (sectionType: NoonReportSectionEnum, noonReport: ValidatedNoonReportBM) => {
    switch (sectionType) {
        case NoonReportSectionEnum.Activities:
            return noonReport.activitiesSection;
        case NoonReportSectionEnum.Bridge:
            return noonReport.bridgeSection;
        case NoonReportSectionEnum.CargoTemperatureControl:
            return noonReport.cargoHeatingSection;
        case NoonReportSectionEnum.Comments:
            return noonReport.commentsSection;
        case NoonReportSectionEnum.Engines:
            return noonReport.enginesSection;
        case NoonReportSectionEnum.FreshWater:
            return noonReport.freshWaterSection;
        case NoonReportSectionEnum.FuelRob:
            return noonReport.fuelRobSection;
        case NoonReportSectionEnum.General:
            return noonReport.generalSection;
        case NoonReportSectionEnum.InertGasProduction:
            return noonReport.inertGasProductionSection;
        case NoonReportSectionEnum.PowerPacks:
            return noonReport.powerpacksSection;
        case NoonReportSectionEnum.TankCleaning:
            return noonReport.tankCleaningSection;
        case NoonReportSectionEnum.Weather:
            return noonReport.weatherSection;
    }
    return;
};

export const setSection = (aolDataItem: AolDataItem, section: any) => {
    if (!aolDataItem.noonReport) return;

    switch (section.sectionData.section) {
        case NoonReportSectionEnum.Activities:
            aolDataItem.noonReport.activitiesSection = noonReportActivitiesSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.Bridge:
            aolDataItem.noonReport.bridgeSection = noonReportBridgeSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.CargoTemperatureControl:
            aolDataItem.noonReport.cargoHeatingSection = noonReportCargoHeatingSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.Comments:
            aolDataItem.noonReport.commentsSection = noonReportCommentsSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.Engines:
            aolDataItem.noonReport.enginesSection = noonReportEnginesSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.FreshWater:
            aolDataItem.noonReport.freshWaterSection = noonReportFreshWaterSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.FuelRob:
            aolDataItem.noonReport.fuelRobSection = noonReportFuelRobSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.General:
            section.noonReportDateTime = aolDataItem.noonReport.generalSection.noonReportDateTime;
            aolDataItem.noonReport.generalSection = noonReportGeneralSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.InertGasProduction:
            aolDataItem.noonReport.inertGasProductionSection = noonReportInertGasProductionSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.Mrv:
            aolDataItem.noonReport.mrvSection = noonReportMrvSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.PowerPacks:
            aolDataItem.noonReport.powerpacksSection = noonReportPowerpacksSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.TankCleaning:
            aolDataItem.noonReport.tankCleaningSection = noonReportTankCleaningSectionFactory.create(section);
            break;
        case NoonReportSectionEnum.Weather:
            aolDataItem.noonReport.weatherSection = noonReportWeatherSectionFactory.create(section);
            break;
    }
};

export const isSectionVisible = (section: NoonReportSectionEnum, selectedAolDataItem: AolDataItem, vessel: Vessel): boolean => {
    if (!selectedAolDataItem) {
        return false;
    }

    if (selectedAolDataItem.reportHours === 0) {
        return section === NoonReportSectionEnum.General;
    }

    if (vessel?.hasOrcaWebSimpleMode) {
        if (section === NoonReportSectionEnum.CargoTemperatureControl) return false;
    }

    if (selectedAolDataItem.aolVersion >= 5 && section === NoonReportSectionEnum.FuelRob) return false;

    const isDryDockingAolReport = selectedAolDataItem.aolDataItemProperties.isDryDockingAolReport;

    switch (selectedAolDataItem.reportType) {
        case NoonReportTypeEnum.Arrival:
            return (
                section === NoonReportSectionEnum.Activities ||
                section === NoonReportSectionEnum.Bridge ||
                section === NoonReportSectionEnum.Engines ||
                (section === NoonReportSectionEnum.FuelRob && hasBunkering(selectedAolDataItem)) ||
                section === NoonReportSectionEnum.General ||
                section === NoonReportSectionEnum.Comments ||
                selectedAolDataItem.isReportAtNoon
            );
        case NoonReportTypeEnum.Departure:
            return (
                section === NoonReportSectionEnum.Activities ||
                section === NoonReportSectionEnum.Bridge ||
                section === NoonReportSectionEnum.Engines ||
                (section === NoonReportSectionEnum.FuelRob && hasBunkering(selectedAolDataItem)) ||
                section === NoonReportSectionEnum.General ||
                section === NoonReportSectionEnum.Comments ||
                selectedAolDataItem.isReportAtNoon
            );
        case NoonReportTypeEnum.Port:
            return (
                (isDryDockingAolReport &&
                    (section === NoonReportSectionEnum.Activities ||
                        section === NoonReportSectionEnum.Engines ||
                        section === NoonReportSectionEnum.FuelRob ||
                        section === NoonReportSectionEnum.General ||
                        section === NoonReportSectionEnum.Comments)) ||
                (!isDryDockingAolReport &&
                    (section === NoonReportSectionEnum.Activities ||
                        section === NoonReportSectionEnum.Bridge ||
                        section === NoonReportSectionEnum.Engines ||
                        (section === NoonReportSectionEnum.FuelRob && hasBunkering(selectedAolDataItem)) ||
                        section === NoonReportSectionEnum.General ||
                        section === NoonReportSectionEnum.Comments ||
                        selectedAolDataItem.isReportAtNoon))
            );
        case NoonReportTypeEnum.Sea:
            return (
                section === NoonReportSectionEnum.Activities ||
                section === NoonReportSectionEnum.Bridge ||
                section === NoonReportSectionEnum.Engines ||
                (section === NoonReportSectionEnum.FuelRob && hasBunkering(selectedAolDataItem)) ||
                section === NoonReportSectionEnum.General ||
                section === NoonReportSectionEnum.Comments ||
                section === NoonReportSectionEnum.Weather ||
                selectedAolDataItem.isReportAtNoon
            );
    }
};

export const getSectionsInColumn = (column: number): NoonReportSectionEnum[] => {
    switch (column) {
        case 1:
            return [NoonReportSectionEnum.Engines];
        case 2:
            return [
                NoonReportSectionEnum.TankCleaning,
                NoonReportSectionEnum.FuelRob,
                NoonReportSectionEnum.FreshWater,
                NoonReportSectionEnum.CargoTemperatureControl,
            ];
        case 3:
            return [
                NoonReportSectionEnum.General,
                NoonReportSectionEnum.Comments,
                NoonReportSectionEnum.Activities,
                NoonReportSectionEnum.Bridge,
                NoonReportSectionEnum.Weather,
                NoonReportSectionEnum.InertGasProduction,
                NoonReportSectionEnum.PowerPacks,
            ];
    }
    return [];
};

export const hasBeenMrvVerified = (mrvVerifiedUntilDate: Date | string | null, aolDate: Date | string | null) => {
    if (mrvVerifiedUntilDate && aolDate && isDateAfter(mrvVerifiedUntilDate, aolDate)) {
        return true;
    }
    return false;
};
